/**
 * The worked-example line + the hover "how it's calculated" text for compare
 * conditions. Two flavours: a REAL example built from a live preview item —
 * the campaign's actual numbers: an item's compared value, the computed
 * level, and whether that item matches right now — and the synthetic
 * fallback using 100 as the sample so the shown number needs no arithmetic
 * (the tag admin page has no campaign attached, so real data can't exist
 * there).
 * For Media Devoted | Created By Ciro Autiero
 */

import { comparePercentFactor } from '@trafficpulse/shared';

import { exampleOpPhrase, type CompareOp } from './compare-sentence.tables';

/** One preview item's numbers, resolved by the caller from the cube bags. */
export interface CompareSample {
  itemName: string;
  /** Compared metric's value over the compare window. */
  rhsValue: number;
  /** The condition's own metric over the rule's window, when present. */
  lhsValue?: number;
}

export interface CompareExampleInput {
  lhsLabel: string;
  op: CompareOp;
  /** Absent = direct comparison (no % active). */
  percent?: number;
  rhsLabel: string;
  /** Window phrase, e.g. "Last 30 days" or "the rule's own window". */
  window: string;
  sample?: CompareSample | null;
}

/** Stats keep sub-unit precision; big numbers get separators. */
export const fmtStat = (n: number): string =>
  n.toLocaleString('en-US', { maximumFractionDigits: Math.abs(n) < 1 ? 4 : 2 });

function matches(lhs: number, op: CompareOp, limit: number): boolean {
  if (op === '<') return lhs < limit;
  if (op === '<=') return lhs <= limit;
  if (op === '>') return lhs > limit;
  if (op === '>=') return lhs >= limit;
  return op === '=' ? lhs === limit : lhs !== limit;
}

function verdictClause(i: CompareExampleInput, limit: number): string {
  const lhs = i.sample?.lhsValue;
  if (lhs === undefined || !Number.isFinite(lhs)) return '';
  const ok = matches(lhs, i.op, limit);
  return ` ${i.lhsLabel} is ${fmtStat(lhs)} right now — ${ok ? 'it matches' : 'no match'}.`;
}

/** "Example: …" from a real item; falls back to the 100-sample sentence. */
export function compareExample(i: CompareExampleInput): string {
  if (!i.sample || !Number.isFinite(i.sample.rhsValue)) return syntheticExample(i);
  const limit = i.sample.rhsValue * comparePercentFactor(i.percent);
  const calc =
    i.percent === undefined
      ? ''
      : `, and ${fmtStat(i.percent)}% of ${fmtStat(i.sample.rhsValue)} is ${fmtStat(limit)}`;
  const phrase = exampleOpPhrase(i.op, fmtStat(limit), false);
  const head = `Example from this campaign: ${i.sample.itemName} has ${i.rhsLabel} (${i.window}) = ${fmtStat(i.sample.rhsValue)}${calc}`;
  return `${head} → matches when ${i.lhsLabel} ${phrase}.${verdictClause(i, limit)}`;
}

function syntheticExample(i: CompareExampleInput): string {
  const limit = fmtStat(100 * comparePercentFactor(i.percent));
  const same = i.lhsLabel === i.rhsLabel;
  const phrase = exampleOpPhrase(i.op, limit, same);
  if (same) {
    return `Example: if ${i.lhsLabel} over ${i.window} was 100, this matches when it's now ${phrase}.`;
  }
  return `Example: if ${i.rhsLabel} over ${i.window} is 100, this matches when ${i.lhsLabel} ${phrase}.`;
}

/** Hover text for the active % control — the calculation, spelled out. */
export function compareCalcTitle(i: CompareExampleInput): string {
  const p = fmtStat(i.percent ?? 100);
  const formula = `How it's calculated: ${i.rhsLabel} (${i.window}) × ${p} ÷ 100 = the level ${i.lhsLabel} is checked against.`;
  if (!i.sample || !Number.isFinite(i.sample.rhsValue)) {
    const limit = fmtStat(100 * comparePercentFactor(i.percent));
    return `${formula} E.g. if ${i.rhsLabel} is 100, that level is ${limit}.`;
  }
  const limit = i.sample.rhsValue * comparePercentFactor(i.percent);
  return `${formula} Here (${i.sample.itemName}): ${fmtStat(i.sample.rhsValue)} × ${p} ÷ 100 = ${fmtStat(limit)}.`;
}
